import { Link, useNavigate } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import {
  combineQueryStates,
  PageHeader,
  QueryErrorPanel,
  Skeleton,
} from '@loop3/ui'
import { isInternalClient } from '@loop3/client-match'
import { humanMonthLong } from '@loop3/dates'
import { useClients } from '@/hooks/useClients'
import { usePeople } from '@/hooks/usePeople'

const PAGE_TITLE = 'Clients'
const PAGE_SUBTITLE = 'Who we work with, since when, and who owns each account.'

/** Client directory: one readable row per client, linking to its business view. */
export function ClientsIndexPage() {
  const clients = useClients()
  const people = usePeople()
  const navigate = useNavigate()
  const { status, retry } = combineQueryStates([clients, people])

  const header = (
    <PageHeader title={PAGE_TITLE} subtitle={PAGE_SUBTITLE} className="mb-6" />
  )

  if (status === 'error')
    return (
      <div className="mx-auto max-w-7xl pb-16">
        {header}
        <QueryErrorPanel onRetry={retry} />
      </div>
    )

  if (status === 'pending')
    return (
      <div className="mx-auto max-w-7xl pb-16">
        {header}
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="border-b border-border/60 py-4">
            <Skeleton className="h-3.5 w-48" />
          </div>
        ))}
      </div>
    )

  const owners = new Map((people.data ?? []).map((p) => [p.id, p.name]))
  const all = clients.data ?? []
  // Loop3's own internal client goes last and reads as internal, not as a customer.
  const list = [
    ...all.filter((c) => !isInternalClient(c)),
    ...all.filter(isInternalClient),
  ]

  return (
    <div className="mx-auto max-w-7xl pb-16">
      {header}
      <table className="w-full table-fixed text-sm">
        <thead>
          <tr className="eyebrow border-b border-border text-left opacity-50">
            <th scope="col" className="px-2 pb-2 font-[inherit]">
              Client
            </th>
            <th scope="col" className="px-2 pb-2 font-[inherit]">
              Client since
            </th>
            <th scope="col" className="px-2 pb-2 font-[inherit]">
              Account owner
            </th>
            <th scope="col" className="w-8">
              <span className="sr-only">Open</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {list.map((c) => {
            const internal = isInternalClient(c)
            return (
              <tr
                key={c.id}
                onClick={() => navigate(`/clients/${c.prefix}`)}
                className={`group cursor-pointer border-b border-border/60 transition-colors hover:bg-[var(--l3-violet-soft)] ${internal ? 'text-muted-foreground' : ''}`}
              >
                <td className="px-2 py-3.5">
                  <span className="flex min-w-0 items-center gap-2">
                    <Link
                      to={`/clients/${c.prefix}`}
                      className="truncate font-medium group-hover:text-[var(--l3-violet)]"
                    >
                      {c.name}
                    </Link>
                    {internal && (
                      <span className="eyebrow shrink-0 rounded border border-border px-1.5 py-0.5 text-[0.6rem] opacity-60">
                        Internal
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-2 py-3.5 opacity-60">
                  {c.since_month ? humanMonthLong(`${c.since_month}-01`) : '—'}
                </td>
                <td className="px-2 py-3.5 opacity-60">
                  {owners.get(c.account_owner_id ?? '') ?? '—'}
                </td>
                <td className="px-2 py-3.5">
                  <ArrowRight className="size-3.5 opacity-0 transition-opacity group-hover:opacity-60" />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {list.length === 0 && (
        <div className="py-6 text-center text-sm opacity-60">
          No clients yet.
        </div>
      )}
    </div>
  )
}
